import React from 'react'
import _, { map, sortBy } from 'lodash'
import Select from 'react-select'
import axios from 'axios'
import PropTypes from 'prop-types'
import {
  ContentSectionBlock,
  FormInputCheckbox,
  FormInputTextArea
} from 'camino-react'

import { csrfToken } from 'Utilities/Csrf'
import Alert from 'Components/Alert'
import WorkEventsInformation from 'Components/Tickets/Resourcing/WorkEventsInformation'

class TicketResourcing extends React.Component {
  constructor (props) {
    super(props)

    const { ticket, queues } = this.props

    this.state = {
      team_id: ticket.team_id,
      queue_id: ticket.queue_id,
      assignee_user_id: _.get(ticket, 'assignee.id'),
      escalation_queue_id: ticket.escalation_queue_id,
      escalation_owner_id: ticket.escalation_owner_id,
      needs_escalation: ticket.needs_escalation,
      escalation_explanation: ticket.escalation_explanation || '',
      showAlert: false,
      flashText: 'Please fill in the required fields',
      fieldsWithErrors: [],
      flashType: 'error',
      queueOptions: []
    }

    this.escalationCssTransition = this.escalationCssTransition.bind(this)
  }

  componentDidMount () {
    const { queues } = this.props

    this.setQueueOptions(queues)
  }

  setQueueOptions (queues) {
    let queueOptions = map(queues, queue => ({ label: queue.name, value: queue.id }))
    queueOptions = sortBy(queueOptions, ({ label }) => label)

    this.setState({ queueOptions })
  }

  setFieldErrorClasses (field) {
    const { fieldsWithErrors } = this.state

    if (_.includes(fieldsWithErrors, field)) {
      return 'error'
    }

    return ''
  }

  handleInputChange (fieldName, value) {
    this.setState({
      [fieldName]: value
    })
  }

  handleEscalationChange (checked) {
    if (checked) {
      this.setState({ needs_escalation: true })
    } else {
      this.setState({
        needs_escalation: false,
        escalation_queue_id: null,
        escalation_owner_id: null,
        escalation_explanation: ''
      })
    }
  }

  handleAssignMe (checked) {
    const { current_user, queues } = this.props
    const { queue_ids: queueIds } = current_user
    const userQueues = _.filter(queues, ({ id }) => queueIds.includes(id))

    if (checked && userQueues.length > 1) {
      this.setState({
        showAlert: true,
        flashText: "You're assigned to more than one queue. Please select queue.",
        flashType: 'info',
        assignee_user_id: current_user.id
      })

      this.setQueueOptions(userQueues)
    } else if (checked) {
      const queue = _.find(queues, ['id', queueIds[0]])

      this.setState({ assignee_user_id: current_user.id, queue_id: queue.id })
    } else {
      this.setState({ assignee_user_id: null, showAlert: false })
      this.setQueueOptions(queues)
    }
  }

  handleSelectChange (fieldName, selectedOption = null) {
    if (_.isNil(selectedOption)) {
      this.setState({ [fieldName]: null })
    } else {
      this.setState({ [fieldName]: selectedOption.value })
    }
  }

  handleUserChange (selectedOption) {
    const { assignee_user_id } = this.state
    const { current_user, queues } = this.props

    if (assignee_user_id === current_user.id) {
      this.handleSelectChange('assignee_user_id', selectedOption)
      this.setQueueOptions(queues)
      this.setState({ showAlert: false })
    } else {
      this.handleSelectChange('assignee_user_id', selectedOption)
    }
  }

  handleEscalationQueueChange (selectedOption) {
    this.handleSelectChange('escalation_queue_id', selectedOption)
    this.handleSelectChange('escalation_owner_id')
  }

  submitForm (event) {
    event.preventDefault()
    this.setState({ isUpdating: true })
    const {
      team_id,
      queue_id,
      assignee_user_id,
      needs_escalation,
      escalation_queue_id,
      escalation_owner_id,
      escalation_explanation
    } = this.state
    const { collectHistory } = this.props

    const patchData = {
      utf8: '√',
      authenticity_token: csrfToken(),
      ticket: {
        queue_id,
        assignee_user_id,
        needs_escalation,
        escalation_queue_id,
        escalation_owner_id,
        escalation_explanation
      }
    }

    axios.patch(`/tickets/${this.props.ticket.id}/resourcing`, patchData)
      .then((response) => {
        this.setState({
          isUpdating: false,
          showAlert: true,
          flashType: 'success',
          flashText: 'Ticket updated.'
        })

        collectHistory()
      })
      .catch((error) => {
        this.setState({
          isUpdating: false,
          showAlert: true,
          fieldsWithErrors: _.keys(error.response.data.errors),
          flashType: 'error',
          flashText: error.response.data.errors
        })
      })
  }

  retrieveErrorMessages () {
    if (_.isArray(this.state.flashText)) {
      return _.map(this.state.flashText, (error, key) => (
        <p key={key}>
          {error}
          <br />
        </p>
      ))
    }

    return this.state.flashText
  }

  escalationCssTransition (maxHeight) {
    const { needs_escalation } = this.state

    return needs_escalation ? {
      transition: 'max-height: 250ms opacity: 500ms ease-out', maxHeight: `${maxHeight}`
    } : {
      display: 'none', transition: 'max-height 500ms, opacity 250ms ease-in', maxHeight: '0'
    }
  }

  render () {
    const {
      isUpdating,
      flashType,
      showAlert,
      queue_id,
      assignee_user_id,
      needs_escalation,
      escalation_queue_id,
      escalation_owner_id,
      escalation_explanation,
      queueOptions
    } = this.state

    const {
      escalationCssTransition
    } = this

    const {
      ticket,
      hidden,
      teams,
      queues,
      current_user,
      events
    } = this.props

    const queueEmployeesOptions = _.map(
      _.get(_.find(queues, ['id', queue_id]), 'users'),
      user => ({ value: user.id, label: user.name })
    )

    const escalationOwnersOptions = _.map(
      _.get(
        _.find(queues, ['id', escalation_queue_id]),
        'users'
      ),
      user => ({ value: user.id, label: user.name })
    )

    return (
      <section className="card card--allow-overflow" style={hidden ? { display: 'none' } : {}}>
        {_.isNull(ticket.primary_ticket_id) && (
          <ContentSectionBlock title="Resourcing">
            <form className="new_ticket" id={`edit_ticket_${ticket.id}`} acceptCharset="UTF-8" onSubmit={event => this.submitForm(event)}>

              <Alert alertType={flashType} showAlert={showAlert}>
                {this.retrieveErrorMessages()}
              </Alert>

              <div className="row">
                <div className="col-12">
                  <label htmlFor="ticket_team_id">
                    Queue
                  </label>
                  <Select
                    id="ticket_queue_id"
                    className={this.setFieldErrorClasses('queue_id')}
                    name="ticket[queue_id]"
                    value={queue_id}
                    onChange={(option) => {
                      this.handleSelectChange('queue_id', option)
                      this.handleUserChange(null)
                    }}
                    options={queueOptions}
                  />
                </div>

                <div className="col-12">
                  <label htmlFor="ticket_assignee_user_id">
                    Owner
                  </label>
                  <Select
                    id="ticket_assignee_user_id"
                    className={this.setFieldErrorClasses('assignee_user_id')}
                    name="ticket[assignee_user_id]"
                    value={_.find(queueEmployeesOptions, ['value', assignee_user_id])}
                    onChange={option => (this.handleUserChange(option))}
                    options={sortBy(queueEmployeesOptions, ({ label }) => label)}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <FormInputCheckbox id="ticket_needs_escalation" onValue={event => (this.handleEscalationChange(event))} value={needs_escalation} name="ticket[needs_escalation]">
                    Needs escalating?
                  </FormInputCheckbox>
                </div>
                <div className="col-12">
                  <FormInputCheckbox id="assignMe" onValue={event => this.handleAssignMe(event)} value={assignee_user_id === current_user.id} name="ticket[assignMe]">
                    Assign Me
                  </FormInputCheckbox>
                </div>
              </div>

              <div
                className="row"
                style={escalationCssTransition('75px')}
              >
                <div className="col-12">
                  <label htmlFor="ticket_escalation_queue_id">
                    Escalation queue
                  </label>
                  <Select
                    id="ticket_escalation_queue_id"
                    className={this.setFieldErrorClasses('escalation_queue_id')}
                    name="ticket[escalation_queue_id]"
                    value={escalation_queue_id}
                    onChange={option => (this.handleEscalationQueueChange(option))}
                    options={queueOptions}
                  />
                </div>

                <div className="col-12">
                  <label htmlFor="ticket_escalation_owner_id">
                  Escalation owner
                  </label>
                  <Select
                    id="ticket_escalation_owner_id"
                    className={this.setFieldErrorClasses('escalation_owner_id')}
                    name="ticket[escalation_owner_id]"
                    value={escalation_owner_id}
                    onChange={option => (this.handleSelectChange('escalation_owner_id', option))}
                    options={sortBy(escalationOwnersOptions, ({ label }) => label)}
                  />
                </div>
              </div>

              <div
                className="row"
                style={escalationCssTransition('150px')}
              >
                <div className="col-24">
                  <label htmlFor="ticket_escalation_explanation" id="ticket_escalation_explanation_label">
                  Why are you escalating this ticket?

                    <FormInputTextArea
                      value={escalation_explanation}
                      onValue={value => (this.handleInputChange('escalation_explanation', value))}
                      className={this.setFieldErrorClasses('escalation_explanation')}
                      id="ticket_escalation_explanation"
                      rows="5"
                    />
                  </label>
                </div>

              </div>

              <div className="form-section">
                <div className="form-section__actions form-section__actions--space-between form-section__actions--no-margin">
                  <a className="button button--secondary form-section__action form-section__action--no-margin" href="javascript:location.reload();">
                  Cancel
                  </a>

                  <input
                    name="commit"
                    value={isUpdating ? 'Updating' : 'Update'}
                    disabled={isUpdating}
                    className="form-section__action form-section__action--no-margin button"
                    id="js-update-ticket-button"
                    type="submit"
                  />
                </div>
              </div>
            </form>
          </ContentSectionBlock>
        )}
        <WorkEventsInformation {...this.props} />
      </section>
    )
  }
}

TicketResourcing.propTypes = {
  collectHistory: PropTypes.func.isRequired
}

export default TicketResourcing
